import {useEffect, RefObject, useCallback} from "react";
import {AgGridReact} from "@ag-grid-community/react";
import {Column} from "@ag-grid-community/core";
import {useShiftPressed} from "./useShiftPressed";

interface UseColorColumnSortControlParams {
  gridRef: RefObject<AgGridReact<any>>;
  gridReady: boolean;
  isInitialized: boolean;
  multipleSortEnabled: boolean;
  sortingColumns: Column[];
  sortableColumnFields: string[];
}

/**
 * Hook to manage color column sorting behavior in AG Grid.
 * When shift key is pressed and multi-sort is enabled:
 * - If color column is being sorted, disable sorting on other columns
 * - If other columns are being sorted, disable sorting on color column
 * When shift key is released, re-enable sorting on all columns
 */
export const useColorColumnSortControl = ({
  gridRef,
  gridReady,
  isInitialized,
  multipleSortEnabled,
  sortingColumns,
  sortableColumnFields
}: UseColorColumnSortControlParams): void => {
  const isShiftKeyPressed = useShiftPressed();

  const resetSortableColumns = useCallback(() => {
    if (!gridRef.current) {
      return;
    }
    const columnApi = gridRef.current.columnApi;
    columnApi.getAllGridColumns()?.forEach((col) => {
      if (sortableColumnFields.includes(col.getColDef().field ?? "")) {
        col.setColDef({...col.getColDef(), sortable: true}, col.getUserProvidedColDef());
      }
    });
  }, [gridRef, sortableColumnFields]);

  useEffect(() => {
    if (!gridReady || !isInitialized || !multipleSortEnabled || !gridRef.current) {
      return;
    }
    const columnApi = gridRef.current.columnApi;
    if (isShiftKeyPressed) {
      const colorCol = columnApi.getColumn("indicatorColor");
      const isColorSorting = sortingColumns.some((col) => col.getColId() === "indicatorColor");

      if (isColorSorting) {
        // Disable sorting on all other sortable columns except color column
        columnApi.getAllGridColumns()?.forEach((col) => {
          const field = col.getColDef().field ?? "";
          if (sortableColumnFields.includes(field) && field !== "indicatorColor") {
            col.setColDef({...col.getColDef(), sortable: false}, col.getUserProvidedColDef());
          }
        });
      } else if (sortingColumns.length > 0) {
        // Disable sorting on color column when other columns are being sorted
        colorCol?.setColDef({...colorCol.getColDef(), sortable: false}, colorCol.getUserProvidedColDef());
      } else {
        resetSortableColumns();
      }
    } else {
      resetSortableColumns();
    }
  }, [
    gridReady,
    isInitialized,
    multipleSortEnabled,
    isShiftKeyPressed,
    gridRef,
    sortableColumnFields,
    sortingColumns,
    resetSortableColumns
  ]);
};
